import React, { useEffect, useMemo, useRef } from 'react';
import gql from 'graphql-tag';
import { withRouter } from 'react-router';
import { useMutation } from '@apollo/client';
import _ from 'lodash';

import { useBridgeApi, useDeviceInfo, useDispatch, useLogger, useSelector } from '~Hooks';
import Journal from '~Utils/journal';
import { v4 as uuid } from 'uuid';

import { createPosPayment, createPosPaymentVariables } from './__queries__';

import * as actions from '~actions';
import OrderSyncController from './OrderSyncController';
import { Meta } from '~reducers';
import Scan from './Methods/Scan';
import IZettle from './Methods/iZettle';
import Timeout from './Methods/Timeout';
import TimeoutTip from './Methods/TimeoutTip';
import Invoice from './Methods/Invoice';
import SwishQR from './Methods/SwishQR';
import Adyen from './Methods/Adyen';
import Microdeb from './Methods/Microdeb';
import Retain24 from './Methods/Retain24';
import WaiterAidGiftcard from './Methods/WaiterAidGiftcard';
import BaemingoGiftCard from './Methods/BaemingoGiftCard';
import AdyenManual from './Methods/AdyenManual';
import Cash from './Methods/Cash';
import PrintedInvoice from './Methods/PrintedInvoice';
import CustomWithTip from './Methods/CustomWithTip';

import { useStorage } from '~Components/Storage';
import IZettleHandyPayment from './Methods/iZettleHandy';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { MethodProps } from './types';
import Crypto from './Methods/Crypto';

const CREATE_POS_PAYMENT = gql`
  mutation createPosPayment($venueId: ID!, $type: String, $staffId: String, $amount: Int!) {
    createPosPayment(
      amount: $amount
      currency: "SEK"
      venueId: $venueId
      staffId: $staffId
      type: $type
    ) {
      success
      payment
      message
    }
  }
`;
interface InstanceProps {
  orderId: string;
  venueId: string;
  paymentId: string;
}
export const PaymentInstance: React.FunctionComponent<InstanceProps> = ({
  orderId,
  venueId,
  paymentId,
}) => {
  const [storage, updateStorage] = useStorage();
  const storageRef = useRef(storage);
  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  const bridgeApi = useBridgeApi();
  const dispatch = useDispatch();
  const deviceInfo = useDeviceInfo();
  const logger = useLogger('payment');

  const staffId = useSelector((state) => state.staff?.id);
  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId))!;
  const splitOrder = useSelector((state) => state.POSSplitOrder);
  const payment = useSelector((state) => state.POSPayments.find((p) => p.id === paymentId))!;
  const request = useSelector((state) =>
    state.paymentBridgeRequests.find((req) => req.paymentId === payment.id),
  );
  const competitions = useCompetition();
  const appVersion = useSelector((state) => state.appVersion); // TEMP-ZETTLE-DISABLE

  const paymentLifecycleOver = useRef(false);

  const orderAmount = _.sumBy(order.items, (o) => o.amount);
  const discount = _.sumBy(order.items, (o) => o.discount);

  const latestSplitPayment = splitOrder?.payments[splitOrder.payments.length - 1];

  const amount =
    payment.amount ?? (!splitOrder ? orderAmount - discount : latestSplitPayment?.amount);

  const [createPosPayment] = useMutation<createPosPayment, createPosPaymentVariables>(
    CREATE_POS_PAYMENT,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const subtotal = useMemo(() => {
    const net = order.items.reduce((sum, item) => {
      const price = item.amount - item.discount;
      return sum + price / (1 + item.vatRate / 100);
    }, 0);

    return net;
  }, [order.items]);

  function onComplete(pid?: string, meta?: Meta) {
    if (paymentLifecycleOver.current) {
      return;
    }

    if (splitOrder) {
      logger.info('Split order payment completed', {
        payment,
        splitOrder,
        latestSplitPayment,
        meta,
      });
    } else {
      logger.info(`Payment completed ${payment.id}`, meta);
    }

    paymentLifecycleOver.current = true;

    // Mark payment as completed so we can give UI feedback
    dispatch({
      type: actions.POS_PAYMENT_COMPLETED,
      id: payment.id,
      meta,
      paymentId: pid,
    });

    bridgeApi.vibrate('notificationSuccess');

    // STAFF COMPETITION: Add tip to staff competition
    const tip = meta?.tip ?? payment.meta.tip;

    if (tip && staffId) {
      competitions.addTip({
        staffId: staffId,
        tip: tip,
      });
    }

    if (order.manualSync) {
      return;
    }

    if (!splitOrder) {
      dispatch({
        type: 'PRINT_RECEIPT',
        id: order.id,
        isInvoicePayment: ['PRINTED_INVOICE', 'INVOICE'].includes(payment.method),
        payment: { ...payment, meta: { ...payment.meta, ...meta } },
        order,
      });

      const data = {
        amount: amount!,
        payment: { ...payment, meta: { ...payment.meta, ...meta } },
        order,
      };

      dispatch({
        type: actions.SYNC_POS_ORDER,
        ...data,
      });

      // Don't print receipt/bong if payment is part of split order

      if (!order.chequeId && !order.bonged) {
        dispatch({
          type: actions.SEND_TABLE_ITEMS,
          items: order.items.filter((item) => !item.isMessage),
          order,
          serviceType: order.serviceType!,
          messages: order.items.filter((item) => item.isMessage),
          origin: order.origin,
        });
      }
    }
  }

  /*
   * The payment was cancelled
   */
  function onCancel() {
    paymentLifecycleOver.current = true;
    Journal.write('Payment', 'Canceled');

    logger.warn(`Payment canceled ${payment.id}`);

    dispatch({
      type: 'REMOVE_POS_PAYMENT',
      id: payment.id,
    });

    if (splitOrder) {
      dispatch({
        type: actions.REMOVE_PAYMENT_FROM_SPLIT_ORDER,
        payment: payment.id,
      });
    }
  }

  /*
   * Backend data like database payment ID changed
   */
  function onMetaChange(meta: Meta) {
    logger.info(`Payment meta changed ${payment.id}`, meta);

    dispatch({
      type: actions.POS_PAYMENT_META,
      id: payment.id,
      meta,
    });
  }

  /*
   * The payment failed.
   */
  function onFail(message?: string) {
    paymentLifecycleOver.current = true;
    Journal.write('Payment', 'Failed');

    logger.warn(`Payment failed ${message ?? ''} (${payment.id})`, {
      payment,
    });

    bridgeApi.vibrate('notificationError');

    dispatch({
      type: actions.POS_PAYMENT_FAILED,
      id: payment.id,
      error: message,
    });
  }

  useEffect(() => {
    if (!amount) {
      return;
    }

    // Only create a payment for SCAN or PAY_LATER. Rest should not pass in payment ID
    if (['SCAN', 'PAY_LATER'].includes(payment.method)) {
      createPosPayment({
        variables: {
          amount,
          type: payment.method,
          venueId,
          staffId,
        },
      }).then(({ data }) => {
        if (data?.createPosPayment.success) {
          onMetaChange({
            paymentId: data.createPosPayment.payment.id,
            amount,
          });
        } else {
          onFail(data?.createPosPayment.message);
          console.error(data?.createPosPayment.message);
        }
      });
    }

    if (payment.method !== 'ADYEN') {
      onMetaChange({
        transactionId: uuid(),
        amount,
      });
    } else {
      onMetaChange({
        // Is replaced with Adyen-generated transactionId after payment is completed
        transactionId: payment.meta.transactionId || orderId,
        amount,
      });
    }
  }, []);

  /*
   * If the payment method has requested a payment from
   * some accessory, send of a bridge request
   */
  useEffect(() => {
    if (
      !request ||
      (!payment.meta.paymentId && !payment.meta.transactionId) ||
      request.status !== 'BOOT'
    ) {
      return;
    }

    let enableTipping = storage.enableTipping;

    if (deviceInfo?.isZettleTerminal) {
      enableTipping = false;
    }

    console.log('POSTING TO BRIDGE');
    window.postMessageBridge({
      payment: {
        id: request.id,
        reference: (payment.meta.transactionId || payment.meta.paymentId)!,
        amount: request.amount,
        enableTipping,
      },
    });

    dispatch({
      type: 'PAYMENT_BRIDGE_REQUEST_SENT',
      id: request.id,
    });

    /**
     * If we dont have a native wrapper this request will
     * hang forever. Solve this in local dev.
     */
    if (!window.hasNativeWrapper && __DEV__) {
      setTimeout(() => {
        dispatch({
          type: 'MESSAGE_PAYMENT_BRIDGE_REQUEST_SUCCESS',
          id: request.id,
          paymentResponse: {
            cardType: 'VISA',
            cardPaymentEntryMode: 'CONTACTLESS_EMV',
            tvr: '000000000',
            maskedPan: '**** **** **** 1873',
            gratuityAmount: 0,
          },
          tips: 500,
        });
      }, 500);
    }
  }, [request && request.status, payment.meta]);

  if (payment.status === 'FAILED') {
    return null;
  }

  if (!payment.meta.paymentId && ['SCAN', 'PAY_LATER'].includes(payment.method)) {
    console.log('No paymentId in meta');
    return null;
  }

  if (amount && !payment.meta.transactionId && ['IZETTLE', 'ADYEN'].includes(payment.method)) {
    console.log('No transactionId in meta');
    return null;
  }

  if (order.orderNumber === undefined) {
    console.log('No orderNumber');
    return null;
  }

  if (!payment.method) {
    logger.warn('No payment method set', {
      paymentId,
      orderId,
    });

    onFail('No payment method set');
    return null;
  }

  const baseProps: MethodProps = {
    payment,
    order,
    amount,
    orderAmount,
    discount,
    subtotal,
    onMetaChange,
    onComplete,
    onFail,
    onCancel,
    venueId,
  };

  if (amount === 0) {
    return <Timeout {...baseProps} />;
  }

  switch (payment.method) {
    case 'SCAN':
      return <Scan {...baseProps} />;
    case 'IZETTLE':
      // appVersion check is temporary as we transition away from our new Zettle payment
      // Joar: The zettle tip UI is broken, so we will use ours for now
      return !deviceInfo?.isZettleTerminal /* || appVersion.charAt(0) !== '4' */ ? ( // TEMP-ZETTLE-DISABLE
        <IZettle {...baseProps} />
      ) : (
        <IZettleHandyPayment {...baseProps} />
      );
    case 'ADYEN':
      return <Adyen {...baseProps} />;
    case 'ADYEN_MANUELL':
      return <AdyenManual {...baseProps} />;
    case 'TIMEOUT_FAIL':
      return <Timeout {...baseProps} fail />;
    case 'SWISH':
      return <CustomWithTip {...baseProps} />;
    case 'SWISH_QR':
      return <SwishQR {...baseProps} />;
    case 'TIMEOUT':
      return <Timeout {...baseProps} />;
    case 'TIMEOUT_TIP':
      return <TimeoutTip {...baseProps} />;
    case 'INVOICE':
      return <Invoice {...baseProps} />;
    case 'MICRODEB':
      return <Microdeb {...baseProps} />;
    case 'CRYPTO':
      return <Crypto {...baseProps} />;
    case 'RETAIN24':
      return <Retain24 {...baseProps} />;
    case 'WAITERAID':
      return <WaiterAidGiftcard {...baseProps} />;
    case 'BAEMINGO_GIFTCARD':
      return <BaemingoGiftCard {...baseProps} />;
    case 'CASH':
      return <Cash {...baseProps} />;
    case 'PRINTED_INVOICE':
      return <PrintedInvoice {...baseProps} />;
    case 'TARJETA': // Spanish manual card payments
      return <CustomWithTip {...baseProps} />;
    default:
      return <Timeout {...baseProps} />;
  }
};

export default withRouter(({ location }: any) => {
  const logger = useLogger('payments-manager');
  const [, , venueId] = location.pathname.split('/');
  const [storage] = useStorage();
  const payments = useSelector((state) => state.POSPayments);
  const orders = useSelector((state) => state.POSOrdersForSync);

  useEffect(() => {
    logger.info('Payments manager mounted', {
      orders: storage.storedOrders,
    });
  }, [storage.storedOrders]);

  return (
    <React.Fragment>
      {payments.map((payment) => (
        <PaymentInstance
          venueId={venueId}
          key={payment.id}
          paymentId={payment.id}
          orderId={payment.order}
        />
      ))}

      {orders.map((order) => (
        <OrderSyncController key={order.order.id} orderId={order.order.id} />
      ))}

      {storage.storedOrders.map((storedOrder) => (
        <OrderSyncController
          key={storedOrder.order.id}
          isStorageOrder
          orderId={storedOrder.order.id}
        />
      ))}
    </React.Fragment>
  );
});

export const PaymentManager: React.FC = ({ children }) => {
  const payments = useSelector((state) => state.POSPayments);

  return (
    <>
      {payments.map((payment) => (
        <PaymentInstance
          venueId={''}
          key={payment.id}
          paymentId={payment.id}
          orderId={payment.order}
        />
      ))}
      {children}
    </>
  );
};
